import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Input, Button } from '@components/common'
import { useProjectWizardStore } from '@store/useProjectWizardStore'
import roleService from '@services/roleService'
import AssociatedRoles from '../../AssociatedRoles'
import '../ProjectWizard.scss'

/**
 * Step 2: Project Scope
 *
 * Collects industry, domain, technology stack, locations, and associated roles
 */
const ProjectScopeStep = ({ onValidChange }) => {
	const {
		projectScope,
		checklistType,
		setIndustry,
		setDomain,
		addTechnology,
		removeTechnology,
		addLocation,
		removeLocation,
		setAssociatedRoles,
		setStartDate,
		setTargetDate,
		setChecklistType,
	} = useProjectWizardStore()

	const [roles, setRoles] = useState([])
	const [loadingRoles, setLoadingRoles] = useState(false)
	const [techInput, setTechInput] = useState('')
	const [locationInput, setLocationInput] = useState('')
	const [errors, setErrors] = useState({})

	// Fetch company roles (system + custom roles)
	useEffect(() => {
		const fetchRoles = async () => {
			setLoadingRoles(true)
			try {
				const response = await roleService.getCompanyAssignableRoles()
				setRoles(response.data || [])
			} catch (error) {
				console.error('Failed to fetch roles:', error)
				setRoles([])
			} finally {
				setLoadingRoles(false)
			}
		}
		fetchRoles()
	}, [])

	// Validate form
	useEffect(() => {
		const newErrors = {}

		// At least industry or domain should be filled
		if (!projectScope.industry.trim() && !projectScope.domain.trim()) {
			newErrors.scope = 'Please provide at least industry or domain'
		}

		// Validate associated roles - at least one role must be selected
		const associatedRoles = projectScope.associatedRoles || []
		if (associatedRoles.length === 0) {
			newErrors.associatedRoles = 'Please select at least one role for this project'
		}

		// Validate dates if both are provided
		if (projectScope.startDate && projectScope.targetDate) {
			if (new Date(projectScope.targetDate) < new Date(projectScope.startDate)) {
				newErrors.targetDate = 'Target date must be after start date'
			}
		}

		setErrors(newErrors)

		const isValid = Object.keys(newErrors).length === 0
		onValidChange && onValidChange(isValid)
	}, [projectScope, onValidChange])

	// Handle adding technology
	const handleAddTechnology = () => {
		const tech = techInput.trim()
		if (tech && !projectScope.technologyStack.includes(tech)) {
			addTechnology(tech)
			setTechInput('')
		}
	}

	// Handle adding location
	const handleAddLocation = () => {
		const loc = locationInput.trim()
		if (loc && !projectScope.geographicalLocations.includes(loc)) {
			addLocation(loc)
			setLocationInput('')
		}
	}

	// Handle associated roles change
	const handleAssociatedRolesChange = (roleIds) => {
		setAssociatedRoles(roleIds)
	}

	// Handle key press for inputs
	const handleKeyPress = (e, action) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			action()
		}
	}

	return (
		<div className="wizard-step">
			<div className="wizard-step__header">
				<h2 className="wizard-step__title">Project Scope</h2>
				<p className="wizard-step__description">
					Define the scope and context of your compliance project
				</p>
			</div>

			<div className="wizard-step__content">
				<div className="form-grid">
					{/* Industry */}
					<div className="form-group">
						<Input
							label="Industry"
							placeholder="e.g., Healthcare, Finance, Technology"
							value={projectScope.industry}
							onChange={(e) => setIndustry(e.target.value)}
						/>
					</div>

					{/* Domain */}
					<div className="form-group">
						<Input
							label="Domain"
							placeholder="e.g., Cloud Services, E-commerce, SaaS"
							value={projectScope.domain}
							onChange={(e) => setDomain(e.target.value)}
						/>
					</div>

					{errors.scope && (
						<div className="form-group form-group--full">
							<span className="form-error">{errors.scope}</span>
						</div>
					)}

					{/* Technology Stack */}
					<div className="form-group">
						<label className="form-label">Technology Stack</label>
						<div className="tag-input-wrapper">
							<div className="tag-input-field">
								<Input
									placeholder="Add technology (e.g., AWS, React, Python)"
									value={techInput}
									onChange={(e) => setTechInput(e.target.value)}
									onKeyDown={(e) => handleKeyPress(e, handleAddTechnology)}
								/>
								<Button
									variant="outline"
									size="small"
									onClick={handleAddTechnology}
									disabled={!techInput.trim()}
								>
									Add
								</Button>
							</div>
							{projectScope.technologyStack.length > 0 && (
								<div className="tag-list">
									{projectScope.technologyStack.map((tech) => (
										<span key={tech} className="tag">
											{tech}
											<button
												type="button"
												className="tag__remove"
												onClick={() => removeTechnology(tech)}
												aria-label={`Remove ${tech}`}
											>
												<i className="icon-close" />
											</button>
										</span>
									))}
								</div>
							)}
						</div>
					</div>

					{/* Geographical Locations */}
					<div className="form-group">
						<label className="form-label">Geographical Locations</label>
						<div className="tag-input-wrapper">
							<div className="tag-input-field">
								<Input
									placeholder="Add location (e.g., USA, Europe, APAC)"
									value={locationInput}
									onChange={(e) => setLocationInput(e.target.value)}
									onKeyDown={(e) => handleKeyPress(e, handleAddLocation)}
								/>
								<Button
									variant="outline"
									size="small"
									onClick={handleAddLocation}
									disabled={!locationInput.trim()}
								>
									Add
								</Button>
							</div>
							{projectScope.geographicalLocations.length > 0 && (
								<div className="tag-list">
									{projectScope.geographicalLocations.map((loc) => (
										<span key={loc} className="tag">
											{loc}
											<button
												type="button"
												className="tag__remove"
												onClick={() => removeLocation(loc)}
												aria-label={`Remove ${loc}`}
											>
												<i className="icon-close" />
											</button>
										</span>
									))}
								</div>
							)}
						</div>
					</div>

					{/* Associated Roles Selection */}
					<div className="form-group form-group--full">
						<label className="form-label">Associated Roles</label>
						<p className="form-helper-text">
							Select the roles that will be involved in this compliance project.
							Team members will be assigned to these roles later when creating tasks.
						</p>
						<AssociatedRoles
							roles={roles}
							selectedRoles={projectScope.associatedRoles || []}
							onChange={handleAssociatedRolesChange}
							loading={loadingRoles}
						/>
						{errors.associatedRoles && <span className="form-error">{errors.associatedRoles}</span>}
					</div>

					{/* Timeline */}
					<div className="form-group">
						<Input
							type="date"
							label="Start Date"
							value={projectScope.startDate}
							onChange={(e) => setStartDate(e.target.value)}
						/>
					</div>

					<div className="form-group">
						<Input
							type="date"
							label="Target Completion Date"
							value={projectScope.targetDate}
							onChange={(e) => setTargetDate(e.target.value)}
							error={errors.targetDate}
						/>
					</div>

					{/* Checklist Type */}
					<div className="form-group form-group--full">
						<label className="form-label">Checklist Type</label>
						<p className="form-helper-text">
							Choose how the compliance checklist will be created for this project.
						</p>
						<div className="checklist-type-options">
							<label className="checklist-type-option">
								<input
									type="checkbox"
									checked={checklistType === 'ai'}
									onChange={() => setChecklistType('ai')}
								/>
								<div className="checklist-type-option__text">
									<span className="checklist-type-option__title">AI Checklist</span>
									<span className="checklist-type-option__desc">
										Automatically generate checklist items using AI based on your selected standards and project scope
									</span>
								</div>
							</label>
							<label className="checklist-type-option">
								<input
									type="checkbox"
									checked={checklistType === 'manual'}
									onChange={() => setChecklistType('manual')}
								/>
								<div className="checklist-type-option__text">
									<span className="checklist-type-option__title">Manual Checklist</span>
									<span className="checklist-type-option__desc">
										Manually add and manage checklist items yourself
									</span>
								</div>
							</label>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

ProjectScopeStep.propTypes = {
	onValidChange: PropTypes.func,
}

export default ProjectScopeStep
